import { ErrorView1 } from '@epihub/web/src/components/ErrorView1';
import { TextGenerateEffect, type WordItem } from '@epihub/web/src/tailwind/components/TextGenerateEffect';
import React from 'react';
import { HoverBorderGradient } from '@epihub/web/src/tailwind/components/Buttons/HoverBorderGradient';
import { IconArrowLeft } from '@tabler/icons-react';
import { styled, useTheme } from '@krishanmadan/frontend/src/theme/theme';
import { useGlobalStore } from '@krishanmadan/frontend/src/store/useGlobalStore';

const BaseText = ({ children }) => <span>{children}</span>;
const Heading = ({ children }) => {
  const theme = useTheme();
  return <h1 className='text-5xl font-bold' style={{ marginTop: 24, color: theme.v0.col.danger.hex }}>{children}</h1>;
};

const errorBoundaryStories: Record<string, Record<'it' | 'en', WordItem[]>> = {
  developer: {
    en: [
      { text: 'A developer deployed to production on a Friday.', Wrapper: BaseText },
      { ms: 1200 },
      { text: 'Monday morning arrived:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Something Went Wrong', Wrapper: Heading },
    ],
    it: [
      { text: 'Uno sviluppatore ha deployato in produzione di venerdì.', Wrapper: BaseText },
      { ms: 1200 },
      { text: 'È arrivato lunedì mattina:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Qualcosa È Andato Storto', Wrapper: Heading },
    ],
  },
  wizard: {
    en: [
      { text: 'A wizard cast a spell of infinite recursion.', Wrapper: BaseText },
      { ms: 800 },
      { text: 'The universe stack overflowed:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Something Went Wrong', Wrapper: Heading },
    ],
    it: [
      { text: 'Un mago lanciò un incantesimo di ricorsione infinita.', Wrapper: BaseText },
      { ms: 800 },
      { text: "Lo stack dell'universo è esploso:", Wrapper: BaseText },
      { ms: 600 },
      { text: 'Qualcosa È Andato Storto', Wrapper: Heading },
    ],
  },
  babel: {
    en: [
      {
        text: 'The Library of Babel tried to index itself.',
        Wrapper: BaseText,
      },
      { ms: 1000 },
      { text: 'The librarians found a note:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Something Went Wrong', Wrapper: Heading },
    ],
    it: [
      {
        text: 'La Biblioteca di Babele provò a indicizzare se stessa.',
        Wrapper: BaseText,
      },
      { ms: 1000 },
      { text: 'I bibliotecari trovarono un biglietto:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Qualcosa È Andato Storto', Wrapper: Heading },
    ],
  },
  philosopher: {
    en: [
      {
        text: 'A philosopher asked: "What happens when an unstoppable process meets an immovable exception?"',
        Wrapper: BaseText,
      },
      { ms: 1000 },
      { text: 'The answer:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Something Went Wrong', Wrapper: Heading },
    ],
    it: [
      {
        text: 'Un filosofo chiese: "Cosa succede quando un processo inarrestabile incontra un\'eccezione inamovibile?"',
        Wrapper: BaseText,
      },
      { ms: 1000 },
      { text: 'La risposta:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Qualcosa È Andato Storto', Wrapper: Heading },
    ],
  },
  schrodinger: {
    en: [
      { text: "Schrödinger's app was both working and crashing.", Wrapper: BaseText },
      { ms: 1000 },
      { text: 'You observed it:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Something Went Wrong', Wrapper: Heading },
    ],
    it: [
      { text: "L'app di Schrödinger funzionava e crashava allo stesso tempo.", Wrapper: BaseText },
      { ms: 1000 },
      { text: "L'hai osservata:", Wrapper: BaseText },
      { ms: 600 },
      { text: 'Qualcosa È Andato Storto', Wrapper: Heading },
    ],
  },
  titanic: {
    en: [
      { text: 'The engineers said this app was unsinkable.', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'It hit an iceberg of edge cases:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Something Went Wrong', Wrapper: Heading },
    ],
    it: [
      { text: "Gli ingegneri dissero che quest'app era inaffondabile.", Wrapper: BaseText },
      { ms: 1000 },
      { text: 'Ha colpito un iceberg di casi limite:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Qualcosa È Andato Storto', Wrapper: Heading },
    ],
  },
  brexit: {
    en: [
      { text: 'Britain asked the EU for a smooth transition.', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'The negotiation process returned:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Something Went Wrong', Wrapper: Heading },
    ],
    it: [
      { text: "La Gran Bretagna chiese all'UE una transizione tranquilla.", Wrapper: BaseText },
      { ms: 1000 },
      { text: 'Il processo di negoziazione restituì:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Qualcosa È Andato Storto', Wrapper: Heading },
    ],
  },
  climate: {
    en: [
      { text: 'World leaders promised to handle the climate crisis.', Wrapper: BaseText },
      { ms: 1000 },
      { text: "Earth's error log updated:", Wrapper: BaseText },
      { ms: 600 },
      { text: 'Something Went Wrong', Wrapper: Heading },
    ],
    it: [
      { text: 'I leader mondiali promisero di gestire la crisi climatica.', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'Il log degli errori della Terra si aggiornò:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Qualcosa È Andato Storto', Wrapper: Heading },
    ],
  },
  ai: {
    en: [
      { text: 'They said AI would solve all our problems.', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'The AI responded:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Something Went Wrong', Wrapper: Heading },
    ],
    it: [
      { text: "Dissero che l'IA avrebbe risolto tutti i nostri problemi.", Wrapper: BaseText },
      { ms: 1000 },
      { text: "L'IA rispose:", Wrapper: BaseText },
      { ms: 600 },
      { text: 'Qualcosa È Andato Storto', Wrapper: Heading },
    ],
  },
  twitter: {
    en: [
      { text: 'Elon promised X would be the everything app.', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'It became the nothing app:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Something Went Wrong', Wrapper: Heading },
    ],
    it: [
      { text: "Elon promise che X sarebbe diventata l'app per tutto.", Wrapper: BaseText },
      { ms: 1000 },
      { text: "È diventata l'app per niente:", Wrapper: BaseText },
      { ms: 600 },
      { text: 'Qualcosa È Andato Storto', Wrapper: Heading },
    ],
  },
  nasa: {
    en: [
      { text: 'NASA used metric. The contractor used imperial.', Wrapper: BaseText },
      { ms: 1200 },
      { text: 'Something Went Wrong', Wrapper: Heading },
    ],
    it: [
      { text: "La NASA usò il sistema metrico. L'appaltatore usò quello imperiale.", Wrapper: BaseText },
      { ms: 1200 },
      { text: 'Qualcosa È Andato Storto', Wrapper: Heading },
    ],
  },
  intern: {
    en: [
      { text: 'They gave the intern production access.', Wrapper: BaseText },
      { ms: 1200 },
      { text: 'Something Went Wrong', Wrapper: Heading },
    ],
    it: [
      { text: "Hanno dato allo stagista l'accesso alla produzione.", Wrapper: BaseText },
      { ms: 1200 },
      { text: 'Qualcosa È Andato Storto', Wrapper: Heading },
    ],
  },
};

const storyKeys = Object.keys(errorBoundaryStories);
const randomStory = () => errorBoundaryStories[storyKeys[Math.floor(Math.random() * storyKeys.length)]];

type Props = {
  error?: any;
  resetErrorBoundary?: () => void;
};

export const ErrorBoundaryView = ({ resetErrorBoundary }: Props) => {
  const language = useGlobalStore((store) => store.settings.language);
  const [story] = React.useState(randomStory);

  const theme = useTheme();
  return (
    <Root>
      <Center>
        <ErrorView1
          mt={48}
          message={(
            <TextGenerateEffect
              words={story[language]}
            />
          )}
          cta={{
            node: (
              <ButtonWrapper>
                <HoverBorderGradient
                  containerClassName='rounded-full'
                  as='button'
                  className='dark:bg-black bg-white text-black dark:text-white flex items-center space-x-2'
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    resetErrorBoundary?.();
                    // Don't use react router's navigate, this wat the page reloads
                    window.location.href = '/';
                  }}
                >
                  <IconArrowLeft color={theme.v0.col.danger.hex} />
                  <span>Home</span>
                </HoverBorderGradient>
              </ButtonWrapper>
            ),
          }}
        />
      </Center>
    </Root>
  );
};

const Root = styled.div`
  height: 75dvh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Center = styled.div`
  max-width: 500px;
  margin-top: 24px;
  @media (max-width: 769px) {
    max-width: 70vw;
  }
  
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const ButtonWrapper = styled.div`
  margin-top: 48px;
  :active {
    transform: scale(0.90);
  }
`;
